import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  CreditCard, 
  DollarSign, 
  Users, 
  Target,
  Search,
  Filter,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  ChevronRight,
  User,
  Building2,
  FileText,
  Upload,
  Download,
  RefreshCw,
  Flag,
  Check,
  X,
  MessageSquare
} from 'lucide-react';

const PaymentProcessing = () => {
  const [activeTab, setActiveTab] = useState('incoming');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [selectedRefund, setSelectedRefund] = useState(null);
  const [showReconcileModal, setShowReconcileModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refundAction, setRefundAction] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [proofFile, setProofFile] = useState(null);
  const [transactionId, setTransactionId] = useState('');

  // Mock data for incoming payments
  const [incomingPayments] = useState([
    {
      id: 'PAY-001',
      transactionId: 'TXN-123456789',
      investorName: 'Ahmad bin Rahman',
      investorType: 'Individual',
      amount: 'MYR 100,000',
      paymentDate: '2024-01-15T09:30:00Z',
      status: 'pending_reconciliation',
      bankReference: 'FTT2024011500123',
      paymentMethod: 'Bank Transfer',
      potential_orders: [
        {
          orderId: 'ORD-001',
          bondName: 'Khazanah Conventional Bond 2026',
          amount: 'MYR 100,000',
          submissionDate: '2024-01-14T16:00:00Z',
          match_score: 100
        }
      ]
    },
    {
      id: 'PAY-002',
      transactionId: 'TXN-987654321',
      investorName: 'Tech Solutions Sdn Bhd',
      investorType: 'Corporate',
      amount: 'MYR 500,000',
      paymentDate: '2024-01-14T14:20:00Z',
      status: 'reconciled',
      bankReference: 'FTT2024011400456',
      paymentMethod: 'Bank Transfer',
      matchedOrder: {
        orderId: 'ORD-002',
        bondName: 'Green Infrastructure Bond 2024-A',
        amount: 'MYR 500,000'
      },
      reconciledDate: '2024-01-14T15:30:00Z',
      reconciledBy: 'custodian.admin@bank.com'
    },
    {
      id: 'PAY-003',
      transactionId: 'TXN-555666777',
      investorName: 'Jennifer Wong Su Ling',
      investorType: 'Individual',
      amount: 'MYR 75,000',
      paymentDate: '2024-01-13T11:45:00Z',
      status: 'discrepancy',
      bankReference: 'FTT2024011300789',
      paymentMethod: 'Bank Transfer',
      discrepancyReason: 'Amount mismatch - Expected MYR 150,000',
      flaggedDate: '2024-01-13T12:00:00Z'
    }
  ]);

  // Mock data for refund requests
  const [refundRequests] = useState([
    {
      id: 'REF-001',
      refundId: 'RF-2024-001',
      investorName: 'Muhammad Ali Hassan',
      investorType: 'Individual',
      amount: 'MYR 50,000',
      reason: 'Failed subscription - verification issues',
      requestDate: '2024-01-12T10:30:00Z',
      status: 'pending_approval',
      originalPayment: {
        transactionId: 'TXN-444555666',
        bondName: 'Corporate Expansion Bond',
        paymentDate: '2024-01-10T09:00:00Z'
      }
    },
    {
      id: 'REF-002',
      refundId: 'RF-2024-002',
      investorName: 'Global Investment Ltd',
      investorType: 'Corporate',
      amount: 'MYR 200,000',
      reason: 'Overpayment - Partial subscription only',
      requestDate: '2024-01-11T15:45:00Z',
      status: 'approved',
      originalPayment: {
        transactionId: 'TXN-333444555',
        bondName: 'Tech Development Sukuk',
        paymentDate: '2024-01-09T11:30:00Z'
      },
      approvedDate: '2024-01-12T09:00:00Z',
      approvedBy: 'custodian.admin@bank.com'
    },
    {
      id: 'REF-003',
      refundId: 'RF-2024-003',
      investorName: 'Lim Seng Choon',
      investorType: 'Individual',
      amount: 'MYR 25,000',
      reason: 'Duplicate payment error',
      requestDate: '2024-01-08T13:20:00Z',
      status: 'processed',
      originalPayment: {
        transactionId: 'TXN-111222333',
        bondName: 'Infrastructure Bond 2022-A',
        paymentDate: '2024-01-07T10:15:00Z'
      },
      approvedDate: '2024-01-09T10:00:00Z',
      processedDate: '2024-01-10T14:30:00Z',
      bankTransferRef: 'REF2024011000123',
      proofDocument: 'refund_proof_REF-003.pdf'
    }
  ]);

  const tabs = [
    {
      id: 'incoming',
      name: 'Incoming Payments',
      icon: DollarSign,
      description: 'Process and reconcile subscription payments'
    },
    {
      id: 'refunds',
      name: 'Refund Requests',
      icon: RefreshCw,
      description: 'Handle refund requests and processing'
    }
  ];

  const filteredPayments = incomingPayments.filter(payment => {
    const matchesSearch = payment.investorName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         payment.transactionId.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         payment.bankReference.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || payment.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const filteredRefunds = refundRequests.filter(refund => {
    const matchesSearch = refund.investorName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         refund.refundId.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         refund.reason.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || refund.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const getPaymentStatusBadge = (status) => {
    const statusClasses = {
      pending_reconciliation: 'bg-yellow-100 text-yellow-800',
      reconciled: 'bg-green-100 text-green-800',
      discrepancy: 'bg-red-100 text-red-800'
    };
    return statusClasses[status] || 'bg-gray-100 text-gray-800';
  };

  const getRefundStatusBadge = (status) => {
    const statusClasses = {
      pending_approval: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-blue-100 text-blue-800',
      processed: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return statusClasses[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending_reconciliation':
      case 'pending_approval':
        return <Clock className="h-4 w-4" />;
      case 'reconciled':
      case 'approved':
      case 'processed':
        return <CheckCircle className="h-4 w-4" />;
      case 'discrepancy':
      case 'rejected':
        return <XCircle className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  const resetModalStates = () => {
    setTransactionId('');
    setRefundReason('');
    setProofFile(null);
  };

  const handleReconcilePayment = (payment) => {
    setSelectedPayment(payment);
    resetModalStates();
    setShowReconcileModal(true);
  };

  const confirmReconciliation = () => {
    console.log('Reconciling payment:', selectedPayment.id, 'with Transaction ID:', transactionId);
    setShowReconcileModal(false);
    resetModalStates();
    // In real implementation, this would call API to reconcile payment
  };

  const handleRefundAction = (refund, action) => {
    setSelectedRefund(refund);
    setRefundAction(action);
    setShowRefundModal(true);
    resetModalStates();
  };

  const confirmRefundAction = () => {
    console.log(`${refundAction} refund:`, selectedRefund.id);
    if (refundAction === 'approve') {
      console.log('Transaction ID for approval:', transactionId);
    } else if (refundAction === 'reject') {
      console.log('Rejection reason:', refundReason);
    } else if (refundAction === 'process') {
      console.log('Proof file:', proofFile);
    }
    setShowRefundModal(false);
    resetModalStates();
    // In real implementation, this would call API
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-2">
              <Link to="/custodian" className="text-gray-400 hover:text-gray-600">
                Custodian Dashboard
              </Link>
              <ChevronRight className="h-4 w-4 text-gray-400" />
              <span className="text-gray-900 font-medium">Payment Processing</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Payment Processing</h1>
          <p className="mt-2 text-gray-600">
            Process subscription payments and handle refund requests with comprehensive reconciliation tools.
          </p>
        </div>

        {/* Navigation Tabs */}
        <div className="border-b border-gray-200 mb-8">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="h-5 w-5 mr-2" />
                  {tab.name}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Incoming Payments Tab */}
        {activeTab === 'incoming' && (
          <div className="space-y-6">
            {/* Filters */}
            <div className="bg-white shadow rounded-lg p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Search Payments
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search by investor, transaction ID..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value="all">All Status</option>
                    <option value="pending_reconciliation">Pending Reconciliation</option>
                    <option value="reconciled">Reconciled</option>
                    <option value="discrepancy">Discrepancy</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      setFilterStatus('all');
                    }}
                    className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            </div>

            {/* Payments Table */}
            <div className="bg-white shadow rounded-lg overflow-hidden overflow-x-auto">
              <table className="table">
                <thead className="table-header">
                  <tr>
                    <th className="table-header-cell">
                      Payment Details
                    </th>
                    <th className="table-header-cell">
                      Investor
                    </th>
                    <th className="table-header-cell">
                      Amount
                    </th>
                    <th className="table-header-cell">
                      Date
                    </th>
                    <th className="table-header-cell">
                      Status
                    </th>
                    <th className="table-header-cell">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredPayments.map((payment) => (
                    <tr key={payment.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {payment.transactionId}
                          </div>
                          <div className="text-sm text-gray-500">
                            {payment.bankReference}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-8 w-8">
                            <div className="h-8 w-8 rounded-full bg-gray-300 flex items-center justify-center">
                              {payment.investorType === 'Individual' ? (
                                <User className="h-4 w-4 text-gray-600" />
                              ) : (
                                <Building2 className="h-4 w-4 text-gray-600" />
                              )}
                            </div>
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900">
                              {payment.investorName}
                            </div>
                            <div className="text-sm text-gray-500">
                              {payment.investorType}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {payment.amount}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(payment.paymentDate)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPaymentStatusBadge(payment.status)}`}>
                          {getStatusIcon(payment.status)}
                          <span className="ml-1 capitalize">{payment.status.replace('_', ' ')}</span>
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          {payment.status === 'pending_reconciliation' && (
                            <button
                              onClick={() => handleReconcilePayment(payment)}
                              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-green-700 bg-green-100 hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                            >
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Reconcile
                            </button>
                          )}
                          {payment.status === 'discrepancy' && (
                            <button className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500">
                              <Flag className="h-3 w-3 mr-1" />
                              Review
                            </button>
                          )}
                          <button className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                            <Eye className="h-3 w-3 mr-1" />
                            View
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {filteredPayments.length === 0 && (
                <div className="text-center py-12">
                  <DollarSign className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No payments found</h3>
                  <p className="text-gray-600">
                    {searchTerm || filterStatus !== 'all'
                      ? 'Try adjusting your search or filter criteria.'
                      : 'No incoming payments to process at this time.'
                    }
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Refunds Tab */}
        {activeTab === 'refunds' && (
          <div className="space-y-6">
            {/* Filters */}
            <div className="bg-white shadow rounded-lg p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Search Refunds
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search by investor, refund ID..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value="all">All Status</option>
                    <option value="pending_approval">Pending Approval</option>
                    <option value="approved">Approved</option>
                    <option value="processed">Processed</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      setFilterStatus('all');
                    }}
                    className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            </div>

            {/* Refunds Table */}
            <div className="bg-white shadow rounded-lg overflow-hidden overflow-x-auto">
              <table className="table">
                <thead className="table-header">
                  <tr>
                    <th className="table-header-cell">
                      Refund Details
                    </th>
                    <th className="table-header-cell">
                      Investor
                    </th>
                    <th className="table-header-cell">
                      Amount
                    </th>
                    <th className="table-header-cell">
                      Reason
                    </th>
                    <th className="table-header-cell">
                      Date
                    </th>
                    <th className="table-header-cell">
                      Status
                    </th>
                    <th className="table-header-cell">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredRefunds.map((refund) => (
                    <tr key={refund.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {refund.refundId}
                          </div>
                          <div className="text-sm text-gray-500">
                            {refund.originalPayment.bondName}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-8 w-8">
                            <div className="h-8 w-8 rounded-full bg-gray-300 flex items-center justify-center">
                              {refund.investorType === 'Individual' ? (
                                <User className="h-4 w-4 text-gray-600" />
                              ) : (
                                <Building2 className="h-4 w-4 text-gray-600" />
                              )}
                            </div>
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900">
                              {refund.investorName}
                            </div>
                            <div className="text-sm text-gray-500">
                              {refund.investorType}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {refund.amount}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 max-w-xs truncate">
                          {refund.reason}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(refund.requestDate)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRefundStatusBadge(refund.status)}`}>
                          {getStatusIcon(refund.status)}
                          <span className="ml-1 capitalize">{refund.status.replace('_', ' ')}</span>
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          {refund.status === 'pending_approval' && (
                            <>
                              <button
                                onClick={() => handleRefundAction(refund, 'approve')}
                                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-green-700 bg-green-100 hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                              >
                                <Check className="h-3 w-3 mr-1" />
                                Approve
                              </button>
                              <button
                                onClick={() => handleRefundAction(refund, 'reject')}
                                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                              >
                                <X className="h-3 w-3 mr-1" />
                                Reject
                              </button>
                            </>
                          )}
                          {refund.status === 'approved' && (
                            <button
                              onClick={() => handleRefundAction(refund, 'process')}
                              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                              <Upload className="h-3 w-3 mr-1" />
                              Process
                            </button>
                          )}
                          {refund.status === 'processed' && refund.proofDocument && (
                            <button className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-gray-700 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500">
                              <Download className="h-3 w-3 mr-1" />
                              Proof
                            </button>
                          )}
                          <button className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                            <Eye className="h-3 w-3 mr-1" />
                            View
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {filteredRefunds.length === 0 && (
                <div className="text-center py-12">
                  <RefreshCw className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No refund requests found</h3>
                  <p className="text-gray-600">
                    {searchTerm || filterStatus !== 'all'
                      ? 'Try adjusting your search or filter criteria.'
                      : 'No refund requests to process at this time.'
                    }
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Reconcile Payment Modal */}
      {showReconcileModal && selectedPayment && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-medium text-gray-900">Reconcile Payment</h3>
                <button
                  onClick={() => {
                    setShowReconcileModal(false);
                    resetModalStates();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="sr-only">Close</span>
                  ×
                </button>
              </div>

              <div className="space-y-6">
                {/* Payment Details */}
                <div className="border rounded-lg p-4 bg-gray-50">
                  <h4 className="font-medium text-gray-900 mb-3">Payment Details</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Transaction ID:</span>
                      <span className="ml-2 font-medium">{selectedPayment.transactionId}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Amount:</span>
                      <span className="ml-2 font-medium">{selectedPayment.amount}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Investor:</span>
                      <span className="ml-2 font-medium">{selectedPayment.investorName}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Date:</span>
                      <span className="ml-2 font-medium">{formatDateTime(selectedPayment.paymentDate)}</span>
                    </div>
                  </div>
                </div>

                {/* Transaction ID Matching */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Transaction ID Matching</h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Enter Transaction ID
                      </label>
                      <input
                        type="text"
                        value={transactionId}
                        onChange={(e) => setTransactionId(e.target.value)}
                        placeholder="Enter the matching transaction ID..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Enter the corresponding transaction ID to reconcile this payment
                      </p>
                    </div>
                    
                    {transactionId && (
                      <div className="border rounded-lg p-4 bg-green-50 border-green-200">
                        <div className="flex items-center">
                          <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                          <div>
                            <p className="text-sm font-medium text-green-900">Ready to Match</p>
                            <p className="text-sm text-green-700">Transaction ID: {transactionId}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowReconcileModal(false);
                    resetModalStates();
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmReconciliation}
                  disabled={!transactionId.trim()}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Confirm Match
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Refund Action Modal */}
      {showRefundModal && selectedRefund && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-lg shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-medium text-gray-900">
                  {refundAction === 'approve' && 'Approve Refund'}
                  {refundAction === 'reject' && 'Reject Refund'}
                  {refundAction === 'process' && 'Process Refund'}
                </h3>
                <button
                  onClick={() => {
                    setShowRefundModal(false);
                    resetModalStates();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="sr-only">Close</span>
                  ×
                </button>
              </div>

              <div className="space-y-4">
                {/* Refund Details */}
                <div className="border rounded-lg p-4 bg-gray-50">
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="text-gray-500">Refund ID:</span>
                      <span className="ml-2 font-medium">{selectedRefund.refundId}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Investor:</span>
                      <span className="ml-2 font-medium">{selectedRefund.investorName}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Amount:</span>
                      <span className="ml-2 font-medium">{selectedRefund.amount}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Reason:</span>
                      <span className="ml-2">{selectedRefund.reason}</span>
                    </div>
                  </div>
                </div>

                {refundAction === 'approve' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Transaction ID Matching
                    </label>
                    <input
                      type="text"
                      value={transactionId}
                      onChange={(e) => setTransactionId(e.target.value)}
                      placeholder="Enter the matching transaction ID..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Enter the corresponding transaction ID to approve this refund
                    </p>
                    {transactionId && (
                      <div className="mt-3 border rounded-lg p-3 bg-green-50 border-green-200">
                        <div className="flex items-center">
                          <CheckCircle className="h-4 w-4 text-green-600 mr-2" />
                          <p className="text-sm text-green-700">Transaction ID: {transactionId}</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {refundAction === 'reject' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Rejection Reason
                    </label>
                    <textarea
                      value={refundReason}
                      onChange={(e) => setRefundReason(e.target.value)}
                      rows={3}
                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      placeholder="Provide detailed reason for rejection..."
                    />
                  </div>
                )}

                {refundAction === 'process' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Upload Proof of Payment
                    </label>
                    <input
                      type="file"
                      onChange={(e) => setProofFile(e.target.files[0])}
                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      accept=".pdf,.jpg,.jpeg,.png"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Upload bank transfer confirmation or payment receipt
                    </p>
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowRefundModal(false);
                    resetModalStates();
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmRefundAction}
                  disabled={
                    (refundAction === 'approve' && !transactionId.trim()) ||
                    (refundAction === 'reject' && !refundReason.trim()) ||
                    (refundAction === 'process' && !proofFile)
                  }
                  className={`px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                    refundAction === 'approve' ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500' :
                    refundAction === 'reject' ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500' :
                    'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
                  }`}
                >
                  {refundAction === 'approve' && 'Approve Refund'}
                  {refundAction === 'reject' && 'Reject Refund'}
                  {refundAction === 'process' && 'Process Refund'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentProcessing; 